import { Fragment, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  IconButton,
  InputAdornment,
  Card,
  CardHeader,
  CardContent,
  Grid,
  Snackbar,
  Typography,
  Link,
} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import { useTheme } from "@mui/material/styles";
import { LoadingButton } from "@mui/lab";
import { Formik, Field } from "formik";
import { useTranslation } from "react-i18next";
import Iconify from "../../../components/Iconify";
import {
  useRegisterMutation,
  useUpdatePasswordMutation,
  useValidateUserMutation,
  useVerifyOTPMutation,
} from "../loginSlice";
import Schema from "./Schema";
import { Message } from "../../../models";
import { RootState } from "../../../store";
import OTPInput from "../../../components/OTPInput";
import { shadow } from "../../../utils/cssStyles";
import { toErrorMsg } from "../../../utils";
import TextField from "../../../components/TextField";

export default () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const language = useSelector((state: RootState) => state.appUser.language);
  const { t } = useTranslation();
  const [registerPost] = useRegisterMutation();
  const [stage, setStage] = useState(1);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showNewCPassword, setShowNewCPassword] = useState(false);
  const [otp, setOtp] = useState("");
  const [userId, setUserId] = useState("");
  const [otpSentTo, setOtpSentTo] = useState("");

  const initialValues: any = {
    userName: "",
    password: "",
    confirmPassword: "",
  };

  const [message, setMessage] = useState<Message | null>(null);

  const [validateUser, { isLoading: isValidatingUser }] =
    useValidateUserMutation();
  const [verifyOTP, { isLoading: isVerifyingOTP }] = useVerifyOTPMutation();
  const [updatePassword, { isLoading: isUpdatingPassword }] =
    useUpdatePasswordMutation();

  const handleValidateUser = async (values: any, actions?: any) => {
    try {
      setMessage(null);
      // actions.setSubmitting(true);
      if (actions) {
        actions.setErrors(null);
      }
      const { isSent, sentTo, error, invalidData }: any = await validateUser({
        userName: values.userName,
      }).unwrap();
      // actions.setSubmitting(false);
      if (isSent) {
        setOtpSentTo(sentTo);
        setStage(2);
        setMessage({
          type: "success",
          message: t("OTPSentSuccessfully", { sentTo }),
        });
      }
      if (error) {
        setMessage({ type: "error", message: toErrorMsg(error) });
      }
      if (invalidData) {
        if (actions) {
          actions.setErrors(null);
        }
        setMessage({ type: "error", message: toErrorMsg(invalidData) });
      }
    } catch (error: any) {
      setMessage({ type: "error", message: error.message });
    }
  };

  const handleVerifyOTP = async (values: any, actions: any) => {
    try {
      setMessage(null);
      // actions.setSubmitting(true);
      actions.setErrors(null);
      if (otp && otp.length === 4) {
        const { isVerified, userId, error, invalidData }: any = await verifyOTP(
          {
            userName: values.userName,
            otp: otp,
          },
        ).unwrap();
        // actions.setSubmitting(false);
        if (isVerified) {
          setUserId(userId);
          setStage(3);
          setMessage({
            type: "success",
            message: t("OTPVerifiedSuccessfully"),
          });
        }
        if (error) {
          setMessage({ type: "error", message: toErrorMsg(error) });
        }
        if (invalidData) {
          actions.setErrors(invalidData);
          setMessage({ type: "error", message: t("Pleasecorrecterrors") });
        }
      } else {
        setMessage({
          type: "error",
          message: t("PleaseEnterValidOTP"),
        });
      }
    } catch (error: any) {
      setMessage({ type: "error", message: error.message });
    }
  };

  const handleUpdatePassword = async (values: any, actions: any) => {
    try {
      setMessage(null);
      // actions.setSubmitting(true);
      actions.setErrors(null);
      if (values.password === values.confirmPassword) {
        const { isChanged, error, invalidData }: any = await updatePassword({
          id: userId,
          password: values.password,
          otp: otp,
        }).unwrap();
        // actions.setSubmitting(false);
        if (isChanged) {
          setMessage({
            type: "success",
            message: t("PasswordChangedSuccessfully"),
          });
          navigate("/login");
        }
        if (error) {
          setMessage({ type: "error", message: toErrorMsg(error) });
        }
        if (invalidData) {
          actions.setErrors(invalidData);
          setMessage({ type: "error", message: t("Pleasecorrecterrors") });
        }
      } else {
        setMessage({
          type: "error",
          message: t("PasswordAndConfirmPassworddosenotmatch"),
        });
      }
    } catch (error: any) {
      setMessage({ type: "error", message: error.message });
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validateOnChange={false}
      validateOnBlur={false}
      validationSchema={Schema(t, stage)}
      onSubmit={async (values, actions) => {
        try {
          setMessage(null);
          if (stage === 1) {
            handleValidateUser(values, actions);
            // setStage(2);
          } else if (stage === 2) {
            handleVerifyOTP(values, actions);
            // setStage(3);
          } else {
            handleUpdatePassword(values, actions);
          }
        } catch (error: any) {
          setMessage({ type: "error", message: error.message });
        }
      }}
    >
      {(props) => (
        <form onSubmit={props.handleSubmit}>
          <Card style={shadow}>
            <CardHeader
              title={
                stage === 1
                  ? t("YourDetails")
                  : stage === 2
                    ? t("EnterOtp", { sentTo: otpSentTo })
                    : t("CreateNewPassword")
              }
              subheader={""}
            />
            <CardContent>
              {stage == 1 && (
                <FormControl size="small" fullWidth sx={{ py: 2 }}>
                  <Field
                    name="userName"
                    render={({ field }: any) => (
                      <TextField
                        field={field}
                        props={props}
                        name="userName"
                        label={t("User Name")}
                        placeholder={t("EnterName")}
                        // required={true}
                        error={!!props?.errors?.userName}
                        helperText={props?.errors?.userName}
                      />
                    )}
                  />
                </FormControl>
              )}

              {stage == 2 && (
                <FormControl
                  size="small"
                  fullWidth
                  sx={{
                    p: 2,
                    alignItems: "center",
                  }}
                >
                  <Field
                    name="otp"
                    render={({ field }: any) => (
                      // <TextField
                      //   field={field}
                      //   props={props}
                      //   name="otp"
                      //   label={t("OTP")}
                      //   placeholder={t("EnterOtp", { sentTo: otpSentTo })}
                      //   required={true}
                      //   error={props?.errors?.otp}
                      //   helperText={props?.errors?.otp}
                      // />
                      <OTPInput
                        otp={otp}
                        onChange={(newValue: string) => {
                          setOtp(newValue);
                        }}
                      />
                    )}
                  />
                </FormControl>
              )}

              {stage == 3 && (
                <Fragment>
                  <FormControl size="small" fullWidth sx={{ p: 2 }}>
                    <Field
                      name="password"
                      render={({ field }: any) => (
                        <TextField
                          field={field}
                          props={props}
                          name="password"
                          label={t("NewPassword")}
                          type={showNewPassword ? "text" : "password"}
                          placeholder={t("EnterNewPassword")}
                          // required={true}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setShowNewPassword(!showNewPassword)
                                  }
                                  edge="end"
                                >
                                  <Iconify
                                    icon={
                                      showNewPassword
                                        ? "eva:eye-fill"
                                        : "eva:eye-off-fill"
                                    }
                                  />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          error={!!props?.errors?.password}
                          helperText={props?.errors?.password}
                          hasVS={false}
                        />
                      )}
                    />
                  </FormControl>

                  <FormControl size="small" fullWidth sx={{ p: 2 }}>
                    <Field
                      name="confirmPassword"
                      render={({ field }: any) => (
                        <TextField
                          field={field}
                          props={props}
                          name="confirmPassword"
                          label={t("ConfirmNewPassword")}
                          type={showNewCPassword ? "text" : "password"}
                          placeholder={t("EnterConfirmNewPassword")}
                          // required={true}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setShowNewCPassword(!showNewCPassword)
                                  }
                                  edge="end"
                                >
                                  <Iconify
                                    icon={
                                      showNewCPassword
                                        ? "eva:eye-fill"
                                        : "eva:eye-off-fill"
                                    }
                                  />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          error={!!props?.errors?.confirmPassword}
                          helperText={props?.errors?.confirmPassword}
                          hasVS={false}
                        />
                      )}
                    />
                  </FormControl>
                </Fragment>
              )}

              <Grid sx={{ mt: "20px" }}>
                <LoadingButton
                  fullWidth
                  size="small"
                  type="submit"
                  variant="contained"
                  loading={
                    isValidatingUser || isVerifyingOTP || isUpdatingPassword
                  }
                  loadingPosition="start"
                >
                  {stage === 1
                    ? t("import.alerts.submitIncomplete.finishButtonTitle")
                    : stage === 2
                      ? t("Verify")
                      : t("Reset")}
                </LoadingButton>
                {stage === 2 && (
                  <Grid>
                    <Typography
                      variant="body2"
                      sx={{ mt: 1, mb: 1, textAlign: "right" }}
                    >
                      <Link
                        variant="subtitle2"
                        sx={{
                          cursor:
                            isValidatingUser ||
                            isVerifyingOTP ||
                            isUpdatingPassword
                              ? "default"
                              : "pointer",
                          color:
                            isValidatingUser ||
                            isVerifyingOTP ||
                            isUpdatingPassword
                              ? "#878787"
                              : theme.palette.primary.main,
                        }}
                        onClick={() => {
                          if (
                            !(
                              isValidatingUser ||
                              isVerifyingOTP ||
                              isUpdatingPassword
                            )
                          ) {
                            handleValidateUser(props.values);
                          }
                        }}
                      >
                        {t("ResendOTP")}
                      </Link>
                    </Typography>
                  </Grid>
                )}
                {message?.type === "error" && (
                  <div
                    style={{
                      marginTop: "15px",
                      color: theme.palette.error.main,
                    }}
                  >
                    {message.message}
                  </div>
                )}

                {message?.type === "success" && (
                  <Snackbar
                    anchorOrigin={{ vertical: "top", horizontal: "center" }}
                    open={true}
                    onClose={() => {
                      setMessage(null);
                    }}
                    message={message.message}
                    key="snackbar-message"
                  />
                )}
              </Grid>
            </CardContent>
          </Card>
        </form>
      )}
    </Formik>
  );
};
